import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Modal, ModalBody, Stack } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import LoadingDots from "./LoadingDots";
import { useTranslation } from "react-i18next";
import { BasicColor } from "../../Utils/constants";
import { cameraIcon } from "../../Utils/SVGs";
import { simpleBase62Decode } from "../../Funcs/Encryption";
import { compressImage } from "../../Funcs/Miscellaneous";

const ChatCameraCaptureButton = ({ onCapture }) => {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const location = useLocation();
  const params = new URLSearchParams(search);
  // const sentMrn = simpleBase62Decode(params.get("mrn"));
  const [isAdmin, setIsAdmin] = useState(false);
  // const [mrn, setMrn] = useState(sentMrn);
  const [imageUrl, setImageUrl] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const videoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const [showLoadingDots, setShowLoadingDots] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  //   const [selectedPtData, setSelectedPtData] = useState(null);
  const [mrnReadOnly, setMrnReadOnly] = useState(true);

  useEffect(() => {
    {
      cameraActive && isIOS && document.getElementById("fileInput").click();
    }
  }, [cameraActive]);

  const stopCamera = () => {
    setShowLoadingDots(false);
    if (mediaStreamRef.current) {
      // Stop all tracks
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop(); // Stop each track (audio, video)
      });
      // Release the media stream
      mediaStreamRef.current = null;
    }

    // If you want to clear the video feed, set the video source to null
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraActive(false); // Ensure UI updates to reflect camera off state
  };

  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setVideoDevices(videoInputs);
        if (videoInputs.length > 0 && !isIOS) {
          setSelectedDeviceId(videoInputs[0].deviceId);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
      }
    };
    getCameras();
  }, [isIOS]);

  useEffect(() => {
    const startCamera = async () => {
      if (cameraActive && videoRef.current) {
        try {
          const constraints = isIOS
            ? { video: { facingMode: useFrontCamera ? "user" : "environment" } }
            : {
                video: {
                  deviceId: selectedDeviceId
                    ? { exact: selectedDeviceId }
                    : undefined,
                },
              };

          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          videoRef.current.srcObject = stream;
          mediaStreamRef.current = stream;
        } catch (error) {
          console.error("Camera access denied:", error);
        }
      }
    };

    if (cameraActive) startCamera();
  }, [cameraActive, selectedDeviceId, useFrontCamera, isIOS]);

  const captureImage = async () => {
    setShowLoadingDots(true);

    // if (mediaStreamRef.current) {
    //   try {
    //     const track = mediaStreamRef.current.getVideoTracks()[0];
    //     const imageCapture = new ImageCapture(track);
    //     const blob = await imageCapture.takePhoto();
    //     const imageDataUrl = URL.createObjectURL(blob);
    //     setCapturedImage(imageDataUrl);
    //     // Pass the captured image to ChatApp
    //     onCapture(imageDataUrl);
    //   } catch (error) {
    //     console.error("Error capturing image:", error);
    //   }
    // }

    if (mediaStreamRef.current) {
      try {
        const track = mediaStreamRef.current.getVideoTracks()[0];
        const imageCapture = new ImageCapture(track);
        const blob = await imageCapture.takePhoto();
        const compressedBlob = await compressImage(blob, 100 * 1024); // Compress to 250 KB
        const imageDataUrl = URL.createObjectURL(compressedBlob);
        setCapturedImage(imageDataUrl);
        onCapture(imageDataUrl); // Pass the image to parent component
      } catch (error) {
        console.error("Error capturing image:", error);
      }
    }

    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 200);
    const cameraSound = document.getElementById("cameraSound");
    cameraSound.play();
    stopCamera();
  };

  const styles = {
    flashOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      zIndex: 9999,
      opacity: 0.8,
      animation: "fadeOut 0.2s ease-in-out",
    },
  };

  const handleSelectLocalImage = (image) => {
    setCapturedImage(image);
  };

  return (
    <div>
      {showLoadingDots && <LoadingDots />}
      {showFlash && <div style={styles.flashOverlay}></div>}
      {cameraActive && !isIOS && (
        <div>
          <Form.Group>
            <Form.Label>{t("SelectCamera")}</Form.Label>
            <Form.Control
              as="select"
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
            >
              {videoDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId}`}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
      )}
      {!cameraActive && (
        <Button
          // variant="primary"
          size="sm"
          style={{ margin: "10px", backgroundColor: BasicColor }}
          onClick={() => {
            setCameraActive(true);
          }}
        >
          {cameraIcon("", 25)}
        </Button>
      )}

      {/* Video Feed and Capture Button */}
      {cameraActive && (
        <div>
          {!isIOS && (
            <video
              ref={videoRef}
              autoPlay
              style={{
                width: "100%",
                height: "auto",
                border: "1px solid black",
                marginTop: "10px",
              }}
            />
          )}
          {!isIOS && (
            <Button
              variant="success"
              onClick={captureImage}
              //   style={{ margin: "10px" }}
            >
              {t("CaptureImage")}
            </Button>
          )}
          <Button variant="dark" onClick={stopCamera}>
            {t("StopCamera")}
          </Button>
        </div>
      )}
      {/* Display DocumentScanner if an image is captured */}

      <audio id="cameraSound" src="/sounds/cameraShutter.mp3"></audio>
      {/* <img style={{ width: "100%" }} src={capturedImage} /> */}
    </div>
  );
};

export default ChatCameraCaptureButton;
